import React, {useMemo, useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary} from '@material-ui/core';
import {ExpandMore} from '@material-ui/icons';
import {FormattedMessage, IntlShape, useIntl} from 'react-intl';
import {DeploymentDetailsInfo} from '../../../types/deployment/DeploymentDetailsInfo';
import {Form} from 'formik';
import {
  ExternalSourceTypeField,
  InputSourceField,
  InputSourceTypeField,
} from './DeploymentDetailsForms';
import {SetUp} from './DeploymentDetails';
import {DashboardNodeEntry} from '../../../api/deployment/DeploymentNodes';
import {GSPProdOperationStatus} from '../../../types/deployment/GSPProdOperationStatus';
import {StreamingDevice} from '../../../types/deployment/StreamingDeviceResponse';
import {StudioTextField} from '../../../base-components/StudioTextField';
import SubAccordion from './SubAccordion';

type DeploymentSettingsInfo = {
  deploymentDetails: DeploymentDetailsInfo;
  setup: SetUp;
  setSetup: React.Dispatch<React.SetStateAction<SetUp>>;
  status: GSPProdOperationStatus | undefined;
  nodes: DashboardNodeEntry[];
  externalStreamingDevices: StreamingDevice[];
};

const DeploymentDetailsSettings = ({
  deploymentDetails,
  setup,
  setSetup,
  status,
  nodes,
  externalStreamingDevices,
}: DeploymentSettingsInfo) => {
  const intl = useIntl();
  return (
    <>
      <DeploymentSettingsAccordion
        deploymentDetails={deploymentDetails}
        setup={setup}
        setSetup={setSetup}
        status={status}
        nodes={nodes}
        externalStreamingDevices={externalStreamingDevices}
        intl={intl}
      />
      <EnvironmentParametersAccordion deploymentDetails={deploymentDetails} intl={intl} />
      <AnalyticsParametersAccordion deploymentDetails={deploymentDetails} intl={intl} />
    </>
  );
};

export default DeploymentDetailsSettings;

type DeploymentSettingsAccordionInfo = DeploymentSettingsInfo & {
  intl: IntlShape;
};

const DeploymentSettingsAccordion = ({
  deploymentDetails,
  setup,
  setSetup,
  status,
  nodes,
  externalStreamingDevices,
  intl,
}: DeploymentSettingsAccordionInfo) => {
  const [deploymentSettingsAccordion, setDeploymentSettingsAccordion] = useState(true);

  return (
    <Accordion
      className="prod-deploy-details__accordion"
      expanded={deploymentSettingsAccordion}
      elevation={0}
      onChange={() => setDeploymentSettingsAccordion(curr => !curr)}
    >
      <AccordionSummary
        className="prod-deploy-details__accordion__title-container"
        expandIcon={<ExpandMore />}
      >
        <FormattedMessage id={'createDeploy.deploymentSettings'} />
      </AccordionSummary>
      <AccordionDetails className="prod-deploy-details__accordion__details">
        <hr />
        <InputSourceAccordion
          deploymentDetails={deploymentDetails}
          setup={setup}
          setSetup={setSetup}
          status={status}
          nodes={nodes}
          externalStreamingDevices={externalStreamingDevices}
          intl={intl}
        />
        <hr />
        <InferenceSettingAccordion deploymentDetails={deploymentDetails} intl={intl} />
        <hr />
        <MLOpsAccordion deploymentDetails={deploymentDetails} intl={intl} />
      </AccordionDetails>
    </Accordion>
  );
};

const InputSourceAccordion = ({
  deploymentDetails,
  setup,
  setSetup,
  status,
  nodes,
  externalStreamingDevices,
  intl,
}: DeploymentSettingsAccordionInfo) => {
  return (
    <SubAccordion title={intl.formatMessage({id: 'createDeploy.inputSource'})}>
      <div className="prod-deploy-details__subaccordion__container">
        {deploymentDetails && (
          <React.Fragment key="prodDeployment_inferenceApp">
            {deploymentDetails && deploymentDetails.internalInputSourcesSupported && (
              <Form
                id="deployment-details-form"
                className="prod-deploy-details__form"
                noValidate
              >
                <InputSourceTypeField
                  disabled={status !== 'IDLE'}
                  setup={setup}
                  setSetup={setSetup}
                />
                <ExternalSourceTypeField
                  disabled={status !== 'IDLE'}
                  setup={setup}
                  setSetup={setSetup}
                />
                <InputSourceField
                  nodes={nodes}
                  externalStreamingDevices={externalStreamingDevices}
                  disabled={status !== 'IDLE'}
                  setup={setup}
                  setSetup={setSetup}
                />
              </Form>
            )}
          </React.Fragment>
        )}
      </div>
    </SubAccordion>
  );
};

type OtherSettingsAccordionInfo = {
  deploymentDetails: DeploymentDetailsInfo;
  intl: IntlShape;
};
const InferenceSettingAccordion = ({
  deploymentDetails,
  intl,
}: OtherSettingsAccordionInfo) => {
  const deploymentParameters = useMemo(() => deploymentDetails?.deploymentParameters, [
    deploymentDetails?.deploymentParameters,
  ]);

  return (
    <SubAccordion title={intl.formatMessage({id: 'createDeploy.inferenceSettings'})}>
      <div className="prod-deploy-details__subaccordion__container">
        {deploymentDetails.applicationPurpose !== 'Classification' ? (
          <div
            id="deployment-settings-readonly-form"
            className="prod-deploy-details__form"
          >
            <div className="disabled">
              <FormattedMessage id={'createDeploy.nmsThreshold'} tagName="label" />
              <h6>{deploymentParameters?.nonMaximumSuppression || 0}</h6>
            </div>
            <div className="disabled">
              <FormattedMessage id={'createDeploy.confThreshold'} tagName="label" />
              <h6>{deploymentParameters?.confThreshold || 0}</h6>
            </div>
          </div>
        ) : (
          <div className="prod-deploy-details__no_settings">
            <FormattedMessage id={'settings.none'} />
          </div>
        )}
      </div>
    </SubAccordion>
  );
};

const MLOpsAccordion = ({deploymentDetails, intl}: OtherSettingsAccordionInfo) => {
  const monitoringParameters = useMemo(() => deploymentDetails?.monitoringParameters, [
    deploymentDetails?.monitoringParameters,
  ]);

  return (
    <SubAccordion title={intl.formatMessage({id: 'createDeploy.mlopsSettings'})}>
      <div className="prod-deploy-details__subaccordion__container">
        {deploymentDetails.applicationPurpose !== 'Classification' ? (
          <div
            id="deployment-settings-readonly-form"
            className="prod-deploy-details__form"
          >
            <div className="disabled">
              <FormattedMessage id={'createDeploy.samplingConfidence'} tagName="label" />
              <h6>{monitoringParameters?.samplingFrequency || 0}</h6>
            </div>
            <div className="disabled">
              <FormattedMessage id={'createDeploy.samplingInterval'} tagName="label" />
              <h6>{monitoringParameters?.samplingFrequency || 0}</h6>
            </div>
            <div className="disabled">
              <FormattedMessage id={'createDeploy.ddaDataMaxSize'} tagName="label" />
              <h6>{monitoringParameters?.ddaDataMaxSize || 0}</h6>
            </div>
          </div>
        ) : (
          <div className="prod-deploy-details__no_settings">
            <FormattedMessage id={'settings.none'} />
          </div>
        )}
      </div>
    </SubAccordion>
  );
};

const EnvironmentParametersAccordion = ({
  deploymentDetails,
}: OtherSettingsAccordionInfo) => {
  const [deploymentSettingsAccordion, setDeploymentSettingsAccordion] = useState(true);

  const deploymentParameters = useMemo(() => deploymentDetails?.deploymentParameters, [
    deploymentDetails?.deploymentParameters,
  ]);
  const environmentParameters = useMemo(() => deploymentParameters?.environment || {}, [
    deploymentParameters?.environment,
  ]);

  const showSettings = useMemo(() => Object.keys(environmentParameters).length, [
    environmentParameters,
  ]);

  return (
    <Accordion
      className="prod-deploy-details__accordion"
      expanded={deploymentSettingsAccordion}
      elevation={0}
      onChange={() => setDeploymentSettingsAccordion(curr => !curr)}
    >
      <AccordionSummary
        className="prod-deploy-details__accordion__title-container"
        expandIcon={<ExpandMore />}
      >
        <FormattedMessage id="environmentParameters.title" />
      </AccordionSummary>
      <AccordionDetails className="prod-deploy-details__accordion__details">
        {showSettings ? (
          <div className="prod-deploy-details__form">
            {Object.keys(environmentParameters).map((value, index) => (
              <StudioTextField
                key={index}
                id="deployment-settings-readonly-form-environment-parameters"
                className="create-prod-deploy__field"
                label={value}
                placeholder={environmentParameters[value]}
                value={environmentParameters[value]}
                disabled={true}
              />
            ))}
          </div>
        ) : (
          <div className="prod-deploy-details__no_settings">
            <FormattedMessage id={'settings.none'} />
          </div>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const AnalyticsParametersAccordion = ({
  deploymentDetails,
}: OtherSettingsAccordionInfo) => {
  const [deploymentSettingsAccordion, setDeploymentSettingsAccordion] = useState(true);

  const deploymentParameters = useMemo(() => deploymentDetails?.deploymentParameters, [
    deploymentDetails?.deploymentParameters,
  ]);
  const analyticsParameters = useMemo(
    () => deploymentParameters?.analyticsParameters || {},
    [deploymentParameters?.analyticsParameters]
  );

  const showSettings = useMemo(() => Object.keys(analyticsParameters).length, [
    analyticsParameters,
  ]);

  return (
    <Accordion
      className="prod-deploy-details__accordion"
      expanded={deploymentSettingsAccordion}
      elevation={0}
      onChange={() => setDeploymentSettingsAccordion(curr => !curr)}
    >
      <AccordionSummary
        className="prod-deploy-details__accordion__title-container"
        expandIcon={<ExpandMore />}
      >
        <FormattedMessage id="analyticsParameters.title" />
      </AccordionSummary>
      <AccordionDetails className="prod-deploy-details__accordion__details">
        {showSettings ? (
          <div className="prod-deploy-details__form">
            {Object.keys(analyticsParameters).map((value, index) => (
              <StudioTextField
                key={index}
                id="deployment-settings-readonly-form-environment-parameters"
                className="create-prod-deploy__field"
                label={value}
                placeholder={analyticsParameters[value]}
                value={analyticsParameters[value]}
                disabled={true}
              />
            ))}
          </div>
        ) : (
          <div className="prod-deploy-details__no_settings">
            <FormattedMessage id={'settings.none'} />
          </div>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
